import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Image, Video, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, [filter]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const params = filter !== "all" ? { category: filter } : {};
      const res = await axios.get(`${API}/gallery`, { params });
      setItems(res.data);
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: "all", label: t("gallery.categories.all") },
    { id: "circuits", label: t("gallery.categories.circuits") },
    { id: "stages", label: t("gallery.categories.stages") },
    { id: "team", label: t("gallery.categories.team") },
    { id: "general", label: t("gallery.categories.general") },
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="gallery-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-12">
        <div className="text-center">
          <span className="text-primary font-medium tracking-widest uppercase text-sm">
            {t("gallery.subtitle")}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-2 mb-6">
            {t("gallery.title")}
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
            {t("gallery.intro")}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={filter === cat.id ? "default" : "outline"}
              onClick={() => setFilter(cat.id)}
              className="rounded-full"
              data-testid={`gallery-filter-${cat.id}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Image className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">Aucun média disponible</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-muted"
                onClick={() => setSelectedItem(item)}
                data-testid={`gallery-item-${item.id}`}
              >
                {item.media_type === "video" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Video className="h-12 w-12 text-white" />
                  </div>
                ) : null}
                <img
                  src={item.thumbnail_url || item.file_url}
                  alt={item.title?.[lang] || item.title?.fr || ""}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-medium truncate">
                      {item.title?.[lang] || item.title?.fr || "Sans titre"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black border-none">
          {selectedItem && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
                onClick={() => setSelectedItem(null)}
              >
                <X className="h-6 w-6" />
              </Button>
              {selectedItem.media_type === "video" ? (
                <video
                  src={selectedItem.file_url}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh] object-contain"
                />
              ) : (
                <img
                  src={selectedItem.file_url}
                  alt={selectedItem.title?.[lang] || ""}
                  className="w-full max-h-[80vh] object-contain"
                />
              )}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80">
                <h3 className="text-white text-xl font-semibold mb-2">
                  {selectedItem.title?.[lang] || selectedItem.title?.fr}
                </h3>
                {selectedItem.description?.[lang] && (
                  <p className="text-white/80">
                    {selectedItem.description[lang]}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
